import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { CreditCard, Check, Loader2, Coins, Zap, Star, TrendingUp, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { createStripeCheckoutSession, verifyStripeSession } from '../utils/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'motion/react';

const CREDIT_PACKAGES = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 5,
    price: 29,
    pricePerCredit: 5.8,
    description: 'Small investor, one-time need',
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
    popular: false,
    savings: null,
  },
  {
    id: 'professional',
    name: 'Professional',
    credits: 20,
    price: 79,
    pricePerCredit: 3.95,
    description: 'Consultant, project developer',
    icon: Star,
    color: 'from-amber-500 to-orange-500',
    popular: true,
    savings: 32,
  },
  {
    id: 'business',
    name: 'Business',
    credits: 100,
    price: 249,
    pricePerCredit: 2.49,
    description: 'EPC firm, portfolio manager',
    icon: TrendingUp,
    color: 'from-purple-500 to-pink-500',
    popular: false,
    savings: 57,
  },
];

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BuyCreditsModal({ isOpen, onClose }: BuyCreditsModalProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { credits, refreshCredits } = useAuth();
  const [purchasing, setPurchasing] = useState<string | null>(null);

  // Check for canceled payment
  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      toast.error('Payment was canceled');
      // Clear URL params
      const currentPath = window.location.pathname;
      navigate(currentPath, { replace: true });
    }
  }, [searchParams]);

  const handlePurchase = async (pkg: typeof CREDIT_PACKAGES[0]) => {
    setPurchasing(pkg.id);

    try {
      // Pass current path for smart redirect after payment
      const currentPath = window.location.pathname;
      
      // Create Stripe Checkout Session
      const { url } = await createStripeCheckoutSession(pkg.id, pkg.credits, pkg.price, currentPath);
      
      if (url) {
        // Redirect to Stripe Checkout
        window.location.href = url;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to initiate checkout');
      setPurchasing(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] cursor-pointer"
          />

          {/* Modal */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl border-2 border-slate-200 shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto relative"
            >
              {/* Close Button - Inside Modal */}
              <button
                onClick={onClose}
                className="absolute top-6 right-6 p-2 rounded-xl transition-colors z-10 bg-slate-100 hover:bg-slate-200 border-2 border-slate-200"
                title="Close"
              >
                <X className="w-5 h-5 text-slate-700" />
              </button>

              <div className="p-6 md:p-10">
                {/* Header */}
                <div className="text-center mb-8">
                  <h2 className="text-4xl font-bold text-slate-900 mb-3">
                    Purchase Credits
                  </h2>
                  <p className="text-slate-600 text-lg mb-2">
                    Each credit allows you to create one complete solar analysis with real NASA data
                  </p>
                  <p className="text-sm text-slate-500">
                    ✓ Real NASA POWER data &nbsp;•&nbsp; ✓ Detailed financial reports &nbsp;•&nbsp; ✓ Professional PDF export
                  </p>
                  
                  {/* Current Balance */}
                  <div className="inline-flex items-center gap-2 mt-5 px-6 py-3 bg-[#4F819F]/10 rounded-xl border-2 border-[#4F819F]/30">
                    <Coins className="w-5 h-5 text-[#4F819F]" />
                    <span className="text-sm text-slate-600">Current Balance:</span>
                    <span className="text-lg font-bold text-[#4F819F]">
                      {credits?.balance ?? 0} credits
                    </span>
                  </div>
                </div>

                {/* Credit Packages */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {CREDIT_PACKAGES.map((pkg, index) => {
                    const Icon = pkg.icon;
                    const isPurchasing = purchasing === pkg.id;
                    const borderClass = pkg.popular 
                      ? 'border-amber-500 ring-4 ring-amber-500/20 scale-105' 
                      : 'border-slate-200';

                    return (
                      <motion.div
                        key={pkg.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`relative bg-white rounded-2xl border-2 shadow-lg overflow-hidden ${borderClass}`}
                      >
                        {/* Popular Badge */}
                        {pkg.popular && (
                          <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg">
                            POPULAR
                          </div>
                        )}

                        {/* Savings Badge */}
                        {pkg.savings && !pkg.popular && (
                          <div className="absolute top-4 right-4 bg-green-600 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg">
                            SAVE {pkg.savings}%
                          </div>
                        )}

                        {/* Savings Badge for Popular */}
                        {pkg.savings && pkg.popular && (
                          <div className="absolute top-16 right-4 bg-green-600 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow-lg">
                            SAVE {pkg.savings}%
                          </div>
                        )}

                        <div className="p-8">
                          {/* Icon */}
                          <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center mb-3 shadow-lg`}>
                            <Icon className="w-8 h-8 text-white" />
                          </div>

                          {/* Package Name */}
                          <h3 className="text-2xl font-bold text-slate-900 mb-1">{pkg.name}</h3>
                          <p className="text-slate-600 text-sm mb-4">{pkg.description}</p>

                          {/* Credits */}
                          <div className="mb-3">
                            <div className="text-4xl font-bold text-slate-900 mb-1">
                              {pkg.credits}
                              <span className="text-lg text-slate-600 font-normal ml-2">credits</span>
                            </div>
                            <div className="text-sm text-slate-500">
                              ${pkg.pricePerCredit.toFixed(2)} per credit
                            </div>
                          </div>

                          {/* Price */}
                          <div className="flex items-baseline mb-4">
                            <span className="text-4xl font-bold text-slate-900">${pkg.price}</span>
                            <span className="text-slate-600 ml-2">USD</span>
                          </div>

                          {/* Features */}
                          <ul className="space-y-2 mb-5">
                            <li className="flex items-start gap-2 text-sm text-slate-700">
                              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>{pkg.credits} complete analyses</span>
                            </li>
                            <li className="flex items-start gap-2 text-sm text-slate-700">
                              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>Unlimited report viewing</span>
                            </li>
                            <li className="flex items-start gap-2 text-sm text-slate-700">
                              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span>Never expires</span>
                            </li>
                          </ul>

                          {/* Purchase Button */}
                          <button
                            onClick={() => handlePurchase(pkg)}
                            disabled={isPurchasing}
                            className={`w-full py-4 rounded-xl font-semibold text-white shadow-lg transition-all text-base ${
                              pkg.popular
                                ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-amber-500/50'
                                : 'bg-gradient-to-r from-[#4F819F] to-[#4E7F9D] hover:from-[#456070] hover:to-[#4F819F] shadow-[#4F819F]/30'
                            } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                          >
                            {isPurchasing ? (
                              <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <CreditCard className="w-5 h-5" />
                                Get {pkg.credits} Credits
                              </>
                            )}
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}